// Google/Apple sign-in inside the desktop app (feature-specs/desktop_app.md
// section 6.2, steps 1 and 7). Google refuses OAuth in embedded browsers, so
// the app opens the user's browser, waits for the one-time code the browser
// hands back, and redeems it at the usual sign-in endpoint in its own session.
import { desktopHasCapability, getDesktopBridge } from "./desktopApp";

export const BROWSER_SIGN_IN_CAPABILITY = "browserSignIn";
// The app keeps one sign-in open for 10 minutes (desktop/src/main/browser_sign_in.js).
export const BROWSER_SIGN_IN_TIMEOUT_MS = 10 * 60 * 1000;

export const BROWSER_SIGN_IN_ERRORS = Object.freeze({
  EXPIRED: "expired",
  SUPERSEDED: "superseded",
  CANCELLED: "cancelled",
  UNAVAILABLE: "unavailable",
  FAILED: "failed",
});

const SIGN_IN_PATHS = {
  google: "/google_create_account",
  apple: "/apple_create_account",
};

export const canUseDesktopBrowserSignIn = () => desktopHasCapability(BROWSER_SIGN_IN_CAPABILITY);

const signInError = (code) => Object.assign(new Error(code), { code });

// Starts a browser sign-in. Returns { result, cancel }: `result` resolves with
// { desktopCode, codeVerifier } once the app receives this request's callback,
// or rejects with an Error whose `code` is one of BROWSER_SIGN_IN_ERRORS.
export const startDesktopBrowserSignIn = (provider, {
  bridge = getDesktopBridge(),
  timeoutMs = BROWSER_SIGN_IN_TIMEOUT_MS,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) => {
  let settled = false;
  let requestId = null;
  let unsubscribe = () => {};
  let timer = null;
  let resolveResult;
  let rejectResult;
  const result = new Promise((resolve, reject) => {
    resolveResult = resolve;
    rejectResult = reject;
  });

  const settle = (callback, value) => {
    if (settled) return;
    settled = true;
    if (timer !== null) clearTimer(timer);
    unsubscribe();
    callback(value);
  };
  const fail = (code) => settle(rejectResult, signInError(code));
  const cancelInApp = () => {
    if (!requestId) return;
    Promise.resolve()
      .then(() => bridge.auth.cancelBrowserSignIn(requestId))
      .catch(() => {});
  };

  const auth = bridge?.auth;
  if (!auth || typeof auth.beginBrowserSignIn !== "function" || typeof auth.onBrowserSignInResult !== "function") {
    fail(BROWSER_SIGN_IN_ERRORS.UNAVAILABLE);
    return { result, cancel: () => {} };
  }

  const handleResult = (payload) => {
    if (payload.requestId !== requestId) return;
    if (payload.desktopCode && payload.codeVerifier) {
      settle(resolveResult, { desktopCode: payload.desktopCode, codeVerifier: payload.codeVerifier });
    } else if (payload.error === BROWSER_SIGN_IN_ERRORS.SUPERSEDED) {
      fail(BROWSER_SIGN_IN_ERRORS.SUPERSEDED);
    } else if (payload.error === BROWSER_SIGN_IN_ERRORS.EXPIRED) {
      fail(BROWSER_SIGN_IN_ERRORS.EXPIRED);
    } else {
      fail(BROWSER_SIGN_IN_ERRORS.FAILED);
    }
  };

  // Listen before starting, so a callback that beats beginBrowserSignIn's
  // reply isn't missed. A result without a requestId belongs to no request.
  const early = [];
  unsubscribe = auth.onBrowserSignInResult((payload) => {
    if (!payload || payload.requestId == null) return;
    if (requestId === null) early.push(payload);
    else handleResult(payload);
  }) || (() => {});

  timer = setTimer(() => {
    cancelInApp();
    fail(BROWSER_SIGN_IN_ERRORS.EXPIRED);
  }, timeoutMs);

  Promise.resolve()
    .then(() => auth.beginBrowserSignIn(provider))
    .then((started) => {
      requestId = started?.requestId || null;
      if (settled) {
        cancelInApp();
        return;
      }
      if (!requestId) {
        fail(BROWSER_SIGN_IN_ERRORS.FAILED);
        return;
      }
      early.splice(0).forEach(handleResult);
    }, () => fail(BROWSER_SIGN_IN_ERRORS.FAILED));

  return {
    result,
    cancel: () => {
      if (settled) return;
      cancelInApp();
      fail(BROWSER_SIGN_IN_ERRORS.CANCELLED);
    },
  };
};

export const desktopSignInRequestBody = ({ desktopCode, codeVerifier, pendingSignupQuestion }) => ({
  desktop_code: desktopCode,
  code_verifier: codeVerifier,
  user: { verified_email: true, subscribed_marketing: true },
  pending_signup_question: pendingSignupQuestion,
});

// Redeems the code at the same endpoint the web buttons use, in the app's own
// session. Resolves with the endpoint's JSON (a user payload or error_message).
export const completeDesktopBrowserSignIn = async ({ provider, desktopCode, codeVerifier, pendingSignupQuestion, fetchImpl = fetch }) => {
  const response = await fetchImpl(SIGN_IN_PATHS[provider], {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(desktopSignInRequestBody({ desktopCode, codeVerifier, pendingSignupQuestion })),
  });
  return response.json();
};
